/************************************************
 File: src/components/AdminPage/AdminPage.js
*************************************************/

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from 'chart.js';

import './AdminPage.css';
import HeaderImage from '../../assets/Images/Header.png';

ChartJS.register(
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
);

function AdminPage({ currentUser }) {
  // Main users
  const [users, setUsers] = useState([]);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);

  // Default branding
  const [defaultLogo, setDefaultLogo] = useState(null);
  const [defaultLogoUrl, setDefaultLogoUrl] = useState('');
  const [defaultSquareLogo, setDefaultSquareLogo] = useState(null);
  const [defaultSquareLogoUrl, setDefaultSquareLogoUrl] = useState('');

  // User account editing
  const [expiryInputs, setExpiryInputs] = useState({});
  const [membershipPlanInputs, setMembershipPlanInputs] = useState({});
  const [openUserId, setOpenUserId] = useState(null);

  // Sub-users
  const [subUsersByUser, setSubUsersByUser] = useState({});
  const [newSubUserEmail, setNewSubUserEmail] = useState({});
  const [newSubUserPassword, setNewSubUserPassword] = useState({});

  // Tabs and statistics
  const [activeTab, setActiveTab] = useState('main');
  const [loginLogs, setLoginLogs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);

  const navigate = useNavigate();

  const adminHeaders = {
    headers: {
      Authorization: `Bearer ${localStorage.getItem('edugroove_access_token')}`,
    },
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(
        'https://edugroove-backend.onrender.com/api/admin/users',
        adminHeaders
      );

      const mainUsers = res.data.filter((user) => user.parentId === null);

      setUsers(mainUsers);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const fetchSubUsersForUser = async (userId) => {
    try {
      const res = await axios.get(
        `https://edugroove-backend.onrender.com/api/users/${userId}/subusers`
      );

      setSubUsersByUser((prev) => ({
        ...prev,
        [userId]: res.data,
      }));
    } catch (err) {
      console.error('Failed to fetch sub-users:', err);
    }
  };

  const fetchDefaultLogo = async () => {
    try {
      const res = await axios.get(
        'https://edugroove-backend.onrender.com/api/admin/getDefaultLogo',
        adminHeaders
      );

      setDefaultLogoUrl(res.data.defaultLogoUrl || '');
    } catch (err) {
      console.error('Failed to fetch default logo:', err);
    }
  };

  const fetchDefaultSquareLogo = async () => {
    try {
      const res = await axios.get(
        'https://edugroove-backend.onrender.com/api/admin/getDefaultLogo?type=square',
        adminHeaders
      );

      setDefaultSquareLogoUrl(res.data.defaultLogoUrl || '');
    } catch (err) {
      console.error('Failed to fetch default square logo:', err);
    }
  };

  const fetchLoginLogs = async () => {
    try {
      const res = await axios.get(
        'https://edugroove-backend.onrender.com/api/admin/login-logs',
        adminHeaders
      );

      setLoginLogs(res.data);
      setCurrentPage(1);
    } catch (err) {
      console.error('Failed to fetch login logs:', err);
    }
  };

  useEffect(() => {
    if (!currentUser?.id || !currentUser?.isAdmin) return;

    fetchUsers();
    fetchDefaultLogo();
    fetchDefaultSquareLogo();
  }, [currentUser?.id, currentUser?.isAdmin]);

  const handleToggleUser = (id) => {
    if (openUserId === id) {
      setOpenUserId(null);
      return;
    }

    setOpenUserId(id);
    fetchSubUsersForUser(id);
  };

  const handleAddUser = async (e) => {
    e.preventDefault();

    try {
      await axios.post(
        'https://edugroove-backend.onrender.com/api/admin/addUser',
        {
          email,
          password,
          isAdmin,
        },
        adminHeaders
      );

      setEmail('');
      setPassword('');
      setIsAdmin(false);

      await fetchUsers();

      alert('Main user added successfully.');
    } catch (err) {
      console.error('Failed to add main user:', err);
      alert('Error adding user.');
    }
  };

  const handleDeleteUser = async (userId) => {
    const confirmed = window.confirm(
      'Are you sure you want to delete this user?'
    );

    if (!confirmed) return;

    try {
      await axios.delete(
        `https://edugroove-backend.onrender.com/api/admin/users/${userId}`,
        adminHeaders
      );

      if (openUserId === userId) {
        setOpenUserId(null);
      }

      await fetchUsers();
    } catch (err) {
      console.error('Failed to delete user:', err);
      alert('Error deleting user.');
    }
  };

  const handleAddSubUser = async (userId) => {
    const subEmail = newSubUserEmail[userId];
    const subPassword = newSubUserPassword[userId];

    if (!subEmail || !subPassword) {
      alert('Please enter both email and password for the sub-user.');
      return;
    }

    try {
      await axios.post(
        `https://edugroove-backend.onrender.com/api/users/${userId}/add-subuser`,
        {
          email: subEmail,
          password: subPassword,
        },
        adminHeaders
      );

      setNewSubUserEmail((prev) => ({
        ...prev,
        [userId]: '',
      }));

      setNewSubUserPassword((prev) => ({
        ...prev,
        [userId]: '',
      }));

      await fetchSubUsersForUser(userId);

      alert('Sub-user added successfully.');
    } catch (err) {
      console.error('Failed to add sub-user:', err);
      alert('Failed to add sub-user.');
    }
  };

  const handleDeleteSubUser = async (userId, subUserId) => {
    const confirmed = window.confirm(
      'Are you sure you want to delete this sub-user?'
    );

    if (!confirmed) return;

    try {
      await axios.delete(
        `https://edugroove-backend.onrender.com/api/subusers/${subUserId}`,
        adminHeaders
      );

      await fetchSubUsersForUser(userId);
    } catch (err) {
      console.error('Failed to delete sub-user:', err);
      alert('Error deleting sub-user.');
    }
  };

  const handleUserLogoChange = async (userId, file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append('logo', file);

    try {
      const res = await axios.post(
        `https://edugroove-backend.onrender.com/api/users/${userId}/logo`,
        formData,
        adminHeaders
      );

      alert('Logo updated.');

      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === userId
            ? {
                ...user,
                logoUrl: res.data.logoUrl || user.logoUrl,
              }
            : user
        )
      );

      await fetchUsers();
    } catch (err) {
      console.error('Failed to update user logo:', err);
      alert('Failed to update logo.');
    }
  };

  const handleUserSquareLogoChange = async (userId, file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append('logo', file);

    try {
      const res = await axios.post(
        `https://edugroove-backend.onrender.com/api/users/${userId}/squareLogo`,
        formData,
        adminHeaders
      );

      alert('Square logo updated.');

      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === userId
            ? {
                ...user,
                squareLogoUrl:
                  res.data.squareLogoUrl || user.squareLogoUrl,
              }
            : user
        )
      );
    } catch (err) {
      console.error('Failed to update square logo:', err);
      alert('Failed to update square logo.');
    }
  };

  const handleDefaultLogoSubmit = async () => {
    if (!defaultLogo) {
      alert('Please choose a rectangular logo first.');
      return;
    }

    const formData = new FormData();
    formData.append('defaultLogo', defaultLogo);

    try {
      const res = await axios.post(
        'https://edugroove-backend.onrender.com/api/admin/setDefaultLogo',
        formData,
        adminHeaders
      );

      setDefaultLogoUrl(res.data.defaultLogoUrl || '');
      setDefaultLogo(null);

      alert('Default rectangular logo updated.');
    } catch (err) {
      console.error('Failed to update default logo:', err);
      alert('Failed to update default logo.');
    }
  };

  const handleDefaultSquareLogoSubmit = async () => {
    if (!defaultSquareLogo) {
      alert('Please choose a square logo first.');
      return;
    }

    const formData = new FormData();
    formData.append('defaultLogo', defaultSquareLogo);
    formData.append('logoType', 'default_square_logo');

    try {
      const res = await axios.post(
        'https://edugroove-backend.onrender.com/api/admin/setDefaultLogo',
        formData,
        adminHeaders
      );

      setDefaultSquareLogoUrl(res.data.defaultLogoUrl || '');
      setDefaultSquareLogo(null);

      alert('Default square logo updated.');
    } catch (err) {
      console.error('Failed to update default square logo:', err);
      alert('Failed to update default square logo.');
    }
  };

  const handleUpdateExpiry = async (userId) => {
    const date = expiryInputs[userId];

    if (!date) {
      alert('Please select a date first.');
      return;
    }

    try {
      await axios.post(
        `https://edugroove-backend.onrender.com/api/admin/users/${userId}/expiry`,
        {
          membershipExpiry: date,
        },
        adminHeaders
      );

      alert('Expiry date updated.');

      await fetchUsers();
    } catch (err) {
      console.error('Failed to update expiry date:', err);
      alert('Failed to update expiry date.');
    }
  };

  const handleUpdateMembershipPlan = async (userId) => {
    const plan = membershipPlanInputs[userId];

    if (plan === undefined || plan === '') {
      alert('Please enter a sub-user limit.');
      return;
    }

    try {
      await axios.post(
        `https://edugroove-backend.onrender.com/api/admin/users/${userId}/plan`,
        {
          subUserLimit: parseInt(plan, 10),
        },
        adminHeaders
      );

      alert('Plan updated.');

      await fetchUsers();
    } catch (err) {
      console.error('Failed to update plan:', err);
      alert('Error updating plan.');
    }
  };

  const filteredLogs = loginLogs.filter((log) => {
    const emailValue = log.email || '';

    const matchesEmail = emailValue
      .toLowerCase()
      .includes(searchQuery.toLowerCase());

    const logDate = new Date(log.loginTime);

    const inStart = startDate
      ? logDate >= new Date(startDate)
      : true;

    const inEnd = endDate
      ? logDate <= new Date(`${endDate}T23:59:59`)
      : true;

    return matchesEmail && inStart && inEnd;
  });

  const totalPages = Math.max(
    1,
    Math.ceil(filteredLogs.length / itemsPerPage)
  );

  const paginatedLogs = filteredLogs.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const loginCountsByDate = {};

  filteredLogs.forEach((log) => {
    const date = new Date(log.loginTime)
      .toISOString()
      .split('T')[0];

    loginCountsByDate[date] =
      (loginCountsByDate[date] || 0) + 1;
  });

  const chartData = {
    labels: Object.keys(loginCountsByDate),
    datasets: [
      {
        label: 'Logins',
        data: Object.values(loginCountsByDate),
        backgroundColor: '#f58220',
        borderRadius: 6,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0,
        },
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
  };

  const renderStatusCircle = (isActive) => (
    <span
      className={`admin-status-dot ${
        isActive ? 'active' : 'inactive'
      }`}
      title={isActive ? 'Clicked' : 'Not clicked'}
      aria-label={isActive ? 'Clicked' : 'Not clicked'}
    />
  );

  if (!currentUser || !currentUser.isAdmin) {
    return (
      <div className="admin-access-denied">
        You must be an admin to view this page.
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-container">
        <header className="admin-page-header">
          <button
            type="button"
            className="admin-home-button"
            onClick={() => navigate('/main')}
          >
            <span aria-hidden="true">←</span>
            Back to Worksheets
          </button>

          <img
            src={HeaderImage}
            alt="EDU Groove"
            className="admin-header-image"
          />

          <div className="admin-title-group">
            <span className="admin-eyebrow">
              EDU Groove Administration
            </span>

            <h1>Admin Panel</h1>

            <p>
              Manage accounts, school branding, membership limits,
              expiry dates and platform activity.
            </p>
          </div>
        </header>

        <section className="admin-tutorial-panel">
          <div className="admin-tutorial-icon" aria-hidden="true">
            i
          </div>

          <div>
            <h2>How this admin area works</h2>

            <p>
              Use the Main tab to create accounts, change default
              branding and manage existing users. Select a user to
              reveal their logos, membership settings and sub-users.
              Use the Stats tab to review logins and export activity.
            </p>
          </div>
        </section>

        <nav className="admin-tabs" aria-label="Admin sections">
          <button
            type="button"
            onClick={() => setActiveTab('main')}
            className={activeTab === 'main' ? 'active' : ''}
          >
            Main Administration
          </button>

          <button
            type="button"
            onClick={() => {
              setActiveTab('stats');
              fetchLoginLogs();
            }}
            className={activeTab === 'stats' ? 'active' : ''}
          >
            Login Statistics
          </button>
        </nav>

        {activeTab === 'main' && (
          <div className="admin-main-content">
            <section className="admin-section-card">
              <div className="admin-section-heading">
                <div>
                  <span className="admin-section-kicker">
                    Account creation
                  </span>
                  <h2>Add New Main User</h2>
                </div>

                <span className="admin-section-badge">
                  New account
                </span>
              </div>

              <form
                onSubmit={handleAddUser}
                className="admin-form"
              >
                <div className="admin-form-grid">
                  <label className="admin-field">
                    <span>Email address</span>

                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="teacher@school.com"
                      required
                    />
                  </label>

                  <label className="admin-field">
                    <span>Temporary password</span>

                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter a password"
                      required
                    />
                  </label>
                </div>

                <label className="admin-checkbox-row">
                  <input
                    type="checkbox"
                    checked={isAdmin}
                    onChange={(e) => setIsAdmin(e.target.checked)}
                  />

                  <span>
                    Give this user full administrator access
                  </span>
                </label>

                <button
                  type="submit"
                  className="admin-primary-button"
                >
                  Add Main User
                </button>
              </form>
            </section>

            <section className="admin-section-card">
              <div className="admin-section-heading">
                <div>
                  <span className="admin-section-kicker">
                    Global branding
                  </span>
                  <h2>Default Logos</h2>
                </div>

                <span className="admin-section-badge">
                  Fallback branding
                </span>
              </div>

              <p className="admin-section-description">
                These logos are used when an account does not have
                its own school branding uploaded.
              </p>

              <div className="admin-logo-grid">
                <article className="admin-logo-card">
                  <div className="admin-logo-card-heading">
                    <div>
                      <h3>Rectangular Logo</h3>
                      <p>Recommended size: 600 × 150 pixels</p>
                    </div>

                    <span className="admin-logo-type">Wide</span>
                  </div>

                  <div className="admin-logo-preview">
                    {defaultLogoUrl ? (
                      <img
                        src={defaultLogoUrl}
                        alt="Current default rectangular logo"
                        className="admin-logo-image admin-logo-image-wide"
                      />
                    ) : (
                      <div className="admin-empty-logo">
                        <span aria-hidden="true">▭</span>
                        <p>No default rectangular logo set</p>
                      </div>
                    )}
                  </div>

                  <label className="admin-file-picker">
                    <span className="admin-file-button">
                      Choose Image
                    </span>

                    <span className="admin-file-name">
                      {defaultLogo
                        ? defaultLogo.name
                        : 'No file selected'}
                    </span>

                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) =>
                        setDefaultLogo(
                          e.target.files?.[0] || null
                        )
                      }
                    />
                  </label>

                  <button
                    type="button"
                    className="admin-primary-button admin-full-button"
                    onClick={handleDefaultLogoSubmit}
                    disabled={!defaultLogo}
                  >
                    Update Default Logo
                  </button>
                </article>

                <article className="admin-logo-card">
                  <div className="admin-logo-card-heading">
                    <div>
                      <h3>Square Logo</h3>
                      <p>Recommended size: 200 × 200 pixels</p>
                    </div>

                    <span className="admin-logo-type">
                      Square
                    </span>
                  </div>

                  <div className="admin-logo-preview">
                    {defaultSquareLogoUrl ? (
                      <img
                        src={defaultSquareLogoUrl}
                        alt="Current default square logo"
                        className="admin-logo-image admin-logo-image-square"
                      />
                    ) : (
                      <div className="admin-empty-logo">
                        <span aria-hidden="true">□</span>
                        <p>No default square logo set</p>
                      </div>
                    )}
                  </div>

                  <label className="admin-file-picker">
                    <span className="admin-file-button">
                      Choose Image
                    </span>

                    <span className="admin-file-name">
                      {defaultSquareLogo
                        ? defaultSquareLogo.name
                        : 'No file selected'}
                    </span>

                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) =>
                        setDefaultSquareLogo(
                          e.target.files?.[0] || null
                        )
                      }
                    />
                  </label>

                  <button
                    type="button"
                    className="admin-primary-button admin-full-button"
                    onClick={handleDefaultSquareLogoSubmit}
                    disabled={!defaultSquareLogo}
                  >
                    Update Default Square Logo
                  </button>
                </article>
              </div>
            </section>

            <section className="admin-section-card">
              <div className="admin-section-heading">
                <div>
                  <span className="admin-section-kicker">
                    User management
                  </span>
                  <h2>All Main Users</h2>
                </div>

                <span className="admin-section-badge">
                  {users.length}{' '}
                  {users.length === 1 ? 'account' : 'accounts'}
                </span>
              </div>

              {users.length === 0 ? (
                <div className="admin-empty-message">
                  No main users were found.
                </div>
              ) : (
                <ul className="admin-user-list">
                  {users.map((user) => {
                    const isOpen = openUserId === user.id;

                    return (
                      <li
                        key={user.id}
                        className={`admin-user-block ${
                          isOpen ? 'open' : ''
                        }`}
                      >
                        <button
                          type="button"
                          className="admin-user-header"
                          onClick={() =>
                            handleToggleUser(user.id)
                          }
                          aria-expanded={isOpen}
                        >
                          <div className="admin-user-summary">
                            <span className="admin-user-email">
                              {user.email}
                            </span>

                            <span className="admin-user-meta">
                              Expires:{' '}
                              {user.membershipExpiry
                                ? user.membershipExpiry.split(
                                    'T'
                                  )[0]
                                : 'Not set'}
                              <span aria-hidden="true"> • </span>
                              Sub-user limit:{' '}
                              {user.subUserLimit ?? 0}
                            </span>
                          </div>

                          <div className="admin-user-header-right">
                            <span
                              className={`admin-role-badge ${
                                user.isAdmin
                                  ? 'admin'
                                  : 'member'
                              }`}
                            >
                              {user.isAdmin
                                ? 'Admin'
                                : 'Member'}
                            </span>

                            <span
                              className="admin-expand-icon"
                              aria-hidden="true"
                            >
                              {isOpen ? '▲' : '▼'}
                            </span>
                          </div>
                        </button>

                        <div className="admin-user-content">
                          <div className="admin-user-settings-grid">
                            <section className="admin-setting-panel">
                              <div className="admin-setting-heading">
                                <div>
                                  <h3>Account Logos</h3>
                                  <p>
                                    Replace this user’s individual
                                    branding.
                                  </p>
                                </div>
                              </div>

                              <div className="admin-user-logo-grid">
                                <div className="admin-user-logo-control">
                                  <span className="admin-control-label">
                                    Rectangular logo
                                  </span>

                                  <div className="admin-small-logo-preview">
                                    {user.logoUrl ? (
                                      <img
                                        src={user.logoUrl}
                                        alt={`${user.email} rectangular logo`}
                                      />
                                    ) : (
                                      <span>
                                        No logo
                                      </span>
                                    )}
                                  </div>

                                  <label className="admin-compact-file-picker">
                                    <span>Choose replacement</span>

                                    <input
                                      type="file"
                                      accept="image/*"
                                      onChange={(e) =>
                                        handleUserLogoChange(
                                          user.id,
                                          e.target.files?.[0]
                                        )
                                      }
                                    />
                                  </label>
                                </div>

                                <div className="admin-user-logo-control">
                                  <span className="admin-control-label">
                                    Square logo
                                  </span>

                                  <div className="admin-small-logo-preview square">
                                    {user.squareLogoUrl ? (
                                      <img
                                        src={
                                          user.squareLogoUrl
                                        }
                                        alt={`${user.email} square logo`}
                                      />
                                    ) : (
                                      <span>
                                        No logo
                                      </span>
                                    )}
                                  </div>

                                  <label className="admin-compact-file-picker">
                                    <span>Choose replacement</span>

                                    <input
                                      type="file"
                                      accept="image/*"
                                      onChange={(e) =>
                                        handleUserSquareLogoChange(
                                          user.id,
                                          e.target.files?.[0]
                                        )
                                      }
                                    />
                                  </label>
                                </div>
                              </div>
                            </section>

                            <section className="admin-setting-panel">
                              <div className="admin-setting-heading">
                                <div>
                                  <h3>Membership Settings</h3>
                                  <p>
                                    Update access expiry and available
                                    teacher seats.
                                  </p>
                                </div>
                              </div>

                              <label className="admin-field">
                                <span>
                                  Membership expiry date
                                </span>

                                <input
                                  type="date"
                                  value={
                                    expiryInputs[user.id] || ''
                                  }
                                  onChange={(e) =>
                                    setExpiryInputs((prev) => ({
                                      ...prev,
                                      [user.id]:
                                        e.target.value,
                                    }))
                                  }
                                />
                              </label>

                              <button
                                type="button"
                                className="admin-secondary-button"
                                onClick={() =>
                                  handleUpdateExpiry(user.id)
                                }
                              >
                                Update Expiry
                              </button>

                              <label className="admin-field admin-field-spacing">
                                <span>
                                  Sub-user limit
                                </span>

                                <input
                                  type="number"
                                  min="0"
                                  value={
                                    membershipPlanInputs[
                                      user.id
                                    ] || ''
                                  }
                                  onChange={(e) =>
                                    setMembershipPlanInputs(
                                      (prev) => ({
                                        ...prev,
                                        [user.id]:
                                          e.target.value,
                                      })
                                    )
                                  }
                                  placeholder={
                                    user.subUserLimit ?? 0
                                  }
                                />
                              </label>

                              <button
                                type="button"
                                className="admin-secondary-button"
                                onClick={() =>
                                  handleUpdateMembershipPlan(
                                    user.id
                                  )
                                }
                              >
                                Update Plan
                              </button>
                            </section>
                          </div>

                          <section className="admin-subuser-section">
                            <div className="admin-setting-heading">
                              <div>
                                <h3>Sub-Users</h3>
                                <p>
                                  Add and remove teachers connected
                                  to this main account.
                                </p>
                              </div>
                            </div>

                            <div className="admin-add-subuser-form">
                              <input
                                type="email"
                                placeholder="Sub-user email"
                                value={
                                  newSubUserEmail[user.id] || ''
                                }
                                onChange={(e) =>
                                  setNewSubUserEmail((prev) => ({
                                    ...prev,
                                    [user.id]:
                                      e.target.value,
                                  }))
                                }
                              />

                              <input
                                type="password"
                                placeholder="Temporary password"
                                value={
                                  newSubUserPassword[user.id] ||
                                  ''
                                }
                                onChange={(e) =>
                                  setNewSubUserPassword(
                                    (prev) => ({
                                      ...prev,
                                      [user.id]:
                                        e.target.value,
                                    })
                                  )
                                }
                              />

                              <button
                                type="button"
                                className="admin-primary-button"
                                onClick={() =>
                                  handleAddSubUser(user.id)
                                }
                              >
                                Add Sub-User
                              </button>
                            </div>

                            {subUsersByUser[user.id] ? (
                              subUsersByUser[user.id].length ===
                              0 ? (
                                <div className="admin-empty-message compact">
                                  No sub-users added yet.
                                </div>
                              ) : (
                                <div className="admin-table-wrapper">
                                  <table className="admin-data-table">
                                    <thead>
                                      <tr>
                                        <th>Email</th>
                                        <th>Action</th>
                                      </tr>
                                    </thead>

                                    <tbody>
                                      {subUsersByUser[
                                        user.id
                                      ].map((subUser) => (
                                        <tr key={subUser.id}>
                                          <td>
                                            {subUser.email}
                                          </td>

                                          <td>
                                            <button
                                              type="button"
                                              className="admin-delete-button"
                                              onClick={() =>
                                                handleDeleteSubUser(
                                                  user.id,
                                                  subUser.id
                                                )
                                              }
                                            >
                                              Delete
                                            </button>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )
                            ) : (
                              <div className="admin-empty-message compact">
                                Loading sub-users...
                              </div>
                            )}
                          </section>

                          <div className="admin-danger-zone">
                            <div>
                              <h3>Delete Main User</h3>
                              <p>
                                This permanently removes the main
                                account.
                              </p>
                            </div>

                            <button
                              type="button"
                              className="admin-delete-button"
                              onClick={() =>
                                handleDeleteUser(user.id)
                              }
                            >
                              Delete User
                            </button>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          </div>
        )}

        {activeTab === 'stats' && (
          <section className="admin-section-card admin-stats-section">
            <div className="admin-section-heading">
              <div>
                <span className="admin-section-kicker">
                  Platform activity
                </span>
                <h2>Login Statistics</h2>
              </div>

              <span className="admin-section-badge">
                {filteredLogs.length}{' '}
                {filteredLogs.length === 1
                  ? 'result'
                  : 'results'}
              </span>
            </div>

            <div className="admin-stats-filters">
              <label className="admin-field">
                <span>Search email</span>

                <input
                  type="text"
                  placeholder="Search by email"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </label>

              <label className="admin-field">
                <span>Start date</span>

                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </label>

              <label className="admin-field">
                <span>End date</span>

                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </label>

              <label className="admin-field">
                <span>Rows per page</span>

                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(
                      parseInt(e.target.value, 10)
                    );
                    setCurrentPage(1);
                  }}
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </label>
            </div>

            <div className="admin-chart-card">
              <div className="admin-chart-heading">
                <div>
                  <h3>Logins by Date</h3>
                  <p>
                    Based on the currently selected filters.
                  </p>
                </div>
              </div>

              <div className="admin-chart-wrapper">
                {filteredLogs.length > 0 ? (
                  <Bar
                    data={chartData}
                    options={chartOptions}
                  />
                ) : (
                  <div className="admin-empty-message">
                    No chart data is available for these
                    filters.
                  </div>
                )}
              </div>
            </div>

            {filteredLogs.length === 0 ? (
              <div className="admin-empty-message">
                No logins match your filters.
              </div>
            ) : (
              <div className="admin-table-wrapper">
                <table className="admin-data-table admin-login-table">
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Main User</th>
                      <th>Time</th>
                      <th>PDF</th>
                      <th>Advanced PDF</th>
                      <th>.Groove</th>
                      <th>IP</th>
                      <th>Location</th>
                    </tr>
                  </thead>

                  <tbody>
                    {paginatedLogs.map((log) => (
                      <tr key={log.id}>
                        <td>{log.email}</td>

                        <td>
                          {log.parentEmail || 'Main User'}
                        </td>

                        <td>
                          {new Date(
                            log.loginTime
                          ).toLocaleString()}
                        </td>

                        <td className="admin-status-cell">
                          {renderStatusCircle(
                            Number(log.exportPdfClicked) === 1
                          )}
                        </td>

                        <td className="admin-status-cell">
                          {renderStatusCircle(
                            Number(
                              log.advancedPdfExportClicked
                            ) === 1
                          )}
                        </td>

                        <td className="admin-status-cell">
                          {renderStatusCircle(
                            Number(
                              log.downloadGrooveClicked
                            ) === 1
                          )}
                        </td>

                        <td>
                          {log.ipAddress || '—'}
                        </td>

                        <td>
                          {log.location ? (
                            <span
                              className="admin-location"
                              title={log.location}
                            >
                              <img
                                src={`https://flagcdn.com/24x18/${log.location
                                  .toLowerCase()
                                  .slice(0, 2)}.png`}
                                alt={log.location}
                              />

                              <span>{log.location}</span>
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="admin-pagination">
              <button
                type="button"
                className="admin-secondary-button"
                onClick={() =>
                  setCurrentPage((page) =>
                    Math.max(1, page - 1)
                  )
                }
                disabled={currentPage === 1}
              >
                Previous
              </button>

              <span>
                Page {currentPage} of {totalPages}
              </span>

              <button
                type="button"
                className="admin-secondary-button"
                onClick={() =>
                  setCurrentPage((page) =>
                    Math.min(totalPages, page + 1)
                  )
                }
                disabled={
                  currentPage === totalPages ||
                  filteredLogs.length === 0
                }
              >
                Next
              </button>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

export default AdminPage;
